/**
 * The account menu: who you are here, your preferences, and the way out.
 *
 * Everything in it existed before — the role line, "Account and password",
 * "Sign out", and the version and environment the sidebar used to show in its
 * footer. The theme and table-density switches are preferences of this
 * browser (src/lib/theme.js) and change nothing on the server.
 *
 * The language is the exception: it is the person's own preference, saved to
 * their profile (LocaleContext.savePreferences), so it follows them to every
 * browser. The interface switches as soon as the server confirms — no reload.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../components/Layout.jsx';
import { Icon } from '../components/Icon.jsx';
import { Popover } from '../components/Popover.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { useOptionalLocale } from '../context/LocaleContext.jsx';
import { useTenant } from '../context/TenantContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { LANGUAGES, normaliseLanguage } from '../i18n/languages.js';
import { useAppearance } from '../hooks/useAppearance.js';
import { useOrgBase } from '../routes/orgNavigation.jsx';

/**
 * What this person is IN THIS ORGANIZATION: "Owner", or the name of their
 * role. (The sidebar used to show this under the organization's name.)
 */
function membershipRole(organization, roles, t) {
  const membership = organization?.membership;
  if (!membership) return null;
  if (membership.isOwner) return t('userMenu.owner');
  return roles.find((role) => role.id === membership.roleId)?.name ?? null;
}

/** Labels: navigation:userMenu.themes.<value> and .themeTitles.<value>. */
const THEME_OPTIONS = [
  { value: 'light', icon: 'sun' },
  { value: 'dark', icon: 'moon' },
  { value: 'system', icon: 'monitor' },
];

const environmentLine = () =>
  import.meta.env.VITE_ENVIRONMENT === 'production'
    ? 'v1.0.0'
    : `v1.0.0 · ${import.meta.env.VITE_ENVIRONMENT ?? 'development'}`;

/** The light / dark / system switch, used here and by the command center. */
export function ThemeSwitch() {
  const { t } = useTranslation('navigation');
  const { preference, setTheme } = useAppearance();
  return (
    <div className="segmented" role="radiogroup" aria-label={t('userMenu.theme')}>
      {THEME_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={preference === option.value}
          className="segmented-option"
          onClick={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            setTheme(option.value, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
          }}
          title={t(`userMenu.themeTitles.${option.value}`)}
        >
          <Icon name={option.icon} size={14} />
          <span>{t(`userMenu.themes.${option.value}`)}</span>
        </button>
      ))}
    </div>
  );
}

function DensitySwitch() {
  const { t } = useTranslation('navigation');
  const { density, setDensity } = useAppearance();
  return (
    <div className="segmented density-switch" role="radiogroup" aria-label={t('userMenu.tableDensity')}>
      {['comfortable', 'compact'].map((value) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={density === value}
          className="segmented-option"
          onClick={() => setDensity(value)}
          // shell.css sizes these through [aria-label='Table density'], which a
          // translated label no longer matches; the same rule, inline.
          style={{ minWidth: 0 }}
        >
          {t(`userMenu.density.${value}`)}
        </button>
      ))}
    </div>
  );
}

/**
 * The person's interface language: their organization's default, or one of
 * the languages chosen for themselves. Each language is named in itself
 * ("العربية", "English"), so it can be found whatever the interface is in.
 * Only inside an organization (where LocaleProvider is).
 */
function LanguageSwitch() {
  const { t } = useTranslation('navigation');
  const locale = useOptionalLocale();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  if (!locale) return null;

  const { userLanguage, organizationLanguage, savePreferences } = locale;
  const chosen = normaliseLanguage(userLanguage); // null: following the organization
  const organizationDefault =
    LANGUAGES.find((language) => language.code === normaliseLanguage(organizationLanguage)) ?? LANGUAGES[0];

  const options = [
    {
      value: null,
      label: t('userMenu.language.organizationDefault', { language: organizationDefault.nativeName }),
    },
    ...LANGUAGES.map((language) => ({ value: language.code, label: language.nativeName, lang: language.code })),
  ];

  const choose = async (value) => {
    if (saving || value === chosen) return;
    setSaving(true);
    try {
      await savePreferences({ preferredLanguage: value });
    } catch (error) {
      toast.error(error?.message || t('userMenu.language.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="account-links" role="radiogroup" aria-label={t('userMenu.language.label')} aria-busy={saving || undefined}>
      <div className="account-pref-label" style={{ padding: '4px 10px 2px' }} aria-hidden="true">
        {t('userMenu.language.label')}
      </div>
      {options.map((option) => {
        const checked = option.value === chosen;
        return (
          <button
            key={option.value ?? 'organization'}
            type="button"
            role="radio"
            aria-checked={checked}
            className="account-link"
            onClick={() => choose(option.value)}
            disabled={saving}
          >
            <span className="grow truncate" lang={option.lang} dir={option.lang ? 'auto' : undefined}>
              {option.label}
            </span>
            {checked && <Icon name="check" size={15} />}
          </button>
        );
      })}
    </div>
  );
}

export function UserMenu({ onShowShortcuts }) {
  const { t } = useTranslation('navigation');
  const { user, activeOrganization, signOut } = useAuth();
  const { organization, roles, loading } = useTenant();
  const navigate = useNavigate();
  const orgBase = useOrgBase();
  const role = loading ? null : membershipRole(organization, roles, t);

  /**
   * What this person is, in the organization they are currently looking at.
   *
   * Deliberately NOT their name: an organization owner is "Administrator"
   * here, and only genuine platform staff are "Platform Administrator".
   */
  const roleLabel = user?.isPlatformAdmin
    ? t('userMenu.platformAdministrator')
    : activeOrganization?.membership?.isOwner
      ? t('userMenu.administrator')
      : null;

  return (
    <Popover
      label={t('userMenu.account')}
      align="end"
      panelClassName="popover-account"
      trigger={({ triggerProps, open }) => (
        <button
          type="button"
          className={`topbar-account ${open ? 'is-open' : ''}`}
          {...triggerProps}
          aria-label={t('userMenu.accountOf', { name: user?.fullName ?? user?.email ?? '' })}
          title={user?.fullName ?? user?.email}
        >
          <Avatar name={user?.fullName} email={user?.email} />
        </button>
      )}
    >
      {({ close }) => (
        <div className="account">
          <div className="account-head">
            <Avatar name={user?.fullName} email={user?.email} large />
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="strong truncate">{user?.fullName}</div>
              <div className="text-sm muted truncate">{user?.email}</div>
              {roleLabel && <div className="account-role">{roleLabel}</div>}
            </div>
          </div>

          {organization?.name && (
            <div className="account-org" title={role ? t('userMenu.yourRoleIn', { organization: organization.name }) : undefined}>
              <Icon name="building" size={14} />
              <span className="truncate">{organization.name}</span>
              {role && <span className="account-org-role">{role}</span>}
            </div>
          )}

          <div className="account-prefs">
            <div className="account-pref">
              <span className="account-pref-label">{t('userMenu.theme')}</span>
              <ThemeSwitch />
            </div>
            <div className="account-pref">
              <span className="account-pref-label">{t('userMenu.tables')}</span>
              <DensitySwitch />
            </div>
          </div>

          <LanguageSwitch />

          <div className="account-links">
            <button
              type="button"
              className="account-link"
              onClick={() => {
                close({ restoreFocus: false });
                navigate(`${orgBase}/account`);
              }}
            >
              <Icon name="key" size={15} />
              {t('items.account.label')}
            </button>
            {/* Everyone's own history; the organization's is in Administration. */}
            <button
              type="button"
              className="account-link"
              onClick={() => {
                close({ restoreFocus: false });
                navigate(`${orgBase}/activity?people=me`);
              }}
            >
              <Icon name="activity-log" size={15} />
              {t('items.activityPeopleMe.label')}
            </button>
            <button
              type="button"
              className="account-link"
              onClick={() => {
                close({ restoreFocus: false });
                onShowShortcuts?.();
              }}
            >
              <Icon name="keyboard" size={15} />
              {t('shortcuts.title')}
            </button>
          </div>

          <div className="account-links">
            <button type="button" className="account-link account-link-danger" onClick={signOut}>
              <Icon name="log-out" size={15} />
              {t('userMenu.signOut')}
            </button>
          </div>

          <div className="account-foot">{environmentLine()}</div>
        </div>
      )}
    </Popover>
  );
}
